import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { IconArrowRight } from "@tabler/icons-react";

import { useDismissAiNotificationMutation } from "../api/aiChatApi";

interface AiOptInReminderDialogProps {
	companyDomainId: string;
	surveyKey: string;
	surveyVersion: number;
	onDismiss: () => void;
}

export function AiOptInReminderDialog({
	companyDomainId,
	surveyKey,
	surveyVersion,
	onDismiss,
}: AiOptInReminderDialogProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [dismissMutation, { isLoading }] = useDismissAiNotificationMutation();
	const explicitActionFired = useRef(false);

	const handleDismiss = async (mode: "snooze" | "permanent") => {
		explicitActionFired.current = true;
		try {
			await dismissMutation({
				companyDomainId,
				surveyKey,
				surveyVersion,
				mode,
			}).unwrap();
			onDismiss();
		} catch {
			explicitActionFired.current = false;
			showErrorNotification({ message: t("Could not dismiss notification") });
		}
	};

	const handleOpen = () => {
		explicitActionFired.current = true;
		void dismissMutation({
			companyDomainId,
			surveyKey,
			surveyVersion,
			mode: "snooze",
		});
		onDismiss();
		navigate(`/company-domains/${companyDomainId}/ai-chat`);
	};

	const handleOpenChange = (isOpen: boolean) => {
		if (isOpen) return;
		if (explicitActionFired.current) {
			explicitActionFired.current = false;
			return;
		}
		void handleDismiss("snooze");
	};

	return (
		<Dialog open onOpenChange={handleOpenChange}>
			<DialogContent size="md">
				<DialogTitle>{t("Discover {{aiProductName}}")}</DialogTitle>
				<DialogBody>
					<div className="flex flex-col gap-5 pb-2">
						<p className="text-gray-700">
							{t(
								"{{aiProductName}} is your financial assistant. Ask questions about your data in natural language and get instant insights.",
							)}{" "}
							{t("You've enabled it but haven't tried it yet.")}
						</p>
						<div className="flex justify-center">
							<Button
								type="button"
								size="lg"
								onClick={handleOpen}
								disabled={isLoading}
								icon={<IconArrowRight />}
								iconPosition="trailing"
							>
								{t("Open {{aiProductName}}")}
							</Button>
						</div>
					</div>
				</DialogBody>
				<DialogActions>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => void handleDismiss("permanent")}
						disabled={isLoading}
					>
						{t("Don't show again")}
					</Button>
					<Button
						type="button"
						variant="secondaryGray"
						size="sm"
						onClick={() => void handleDismiss("snooze")}
						disabled={isLoading}
					>
						{t("Remind me later")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
